/**
 * Renders an Ask ABHI answer.
 *
 * A small markdown dialect the assistant is prompted to use (see
 * hy_sales/services/chat/prompts.py, "## Format"):
 *
 *   - paragraphs (14px); a short one-line opener is the headline (15px semibold)
 *   - `### Heading`, bullet / numbered lists, **bold**, *italic*, `code`
 *   - tables — numeric columns right-aligned, signed changes coloured, a faint
 *     bar behind the main measure
 *   - `:::kpi` blocks — one stat tile per line: `Label | value | +12.3% [S1]`
 *   - `:::warning` / `:::note` callouts
 *   - a trailing `_Assumed: …_` line, shown as a footnote
 *   - signed changes anywhere (`+75%`, `-8.4%`) coloured green / red
 *
 * Everything is built as React elements; nothing goes through innerHTML,
 * so answer text (which can quote rep-written notes) can never inject
 * markup. Citation markers like `[S1]` or `[S1, N482]` become small named
 * source pills ("FL · YoY ↗") that call `onCite`.
 */

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Tooltip, Typography } from '@mui/material';
import { Fragment, type ReactNode } from 'react';

import type { ChatCitation } from '../../api/chat';
import {
  ACCENT,
  ACCENT_DEEP,
  ACCENT_SOFT,
  ACCENT_TINT,
  FS,
  INK,
  LINE,
  LINK,
  LINK_BG_HOVER,
  MUTED,
  PANEL,
  SUBTLE,
  SURFACE,
  TEXT,
} from './chatTheme';

interface Props {
  text: string;
  /** Source id → display number (1-based, first-cited order). */
  citeNumbers: Map<string, number>;
  /** Source id → citation, for the chip's name and hover detail. */
  sources: Map<string, ChatCitation>;
  onCite: (id: string) => void;
  /** Sources already shown as a chip in this answer (set internally). */
  seen?: Set<string>;
}

const CITE_RE = /\[((?:[SN]\d+)(?:\s*,\s*[SN]\d+)*)\]/g;
const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|_[^_\s][^_]*_|`[^`]+`)/g;
/** An explicitly signed change: +75%, -8.4%, −120.5. Needs a leading space or start. */
const SIGNED_RE = /((?:^|(?<=[\s(]))[+\-−]\d[\d,]*(?:\.\d+)?%?)/g;
const NUMERIC_CELL = /^[+\-−$]?\d[\d,]*(?:\.\d+)?%?$/;

/** Longest opening line still styled as a headline. */
const HEADLINE_MAX = 180;

const UP = '#047857';
const DOWN = '#b91c1c';

function signOf(token: string): 1 | -1 | 0 {
  const t = token.trim();
  if (t.startsWith('+')) return 1;
  if (t.startsWith('-') || t.startsWith('−')) return /^[-−]0(\.0+)?%?$/.test(t) ? 0 : -1;
  return 0;
}

/**
 * Inline source pill, ChatGPT-style: a quiet grey tag with the source's short
 * name and a ↗ — it opens the rows behind the claim. Hover shows a small card
 * with the full title and filters.
 */
function CiteChip({ source, onClick }: { source: ChatCitation; onClick: () => void }) {
  const Icon = source.kind === 'note' ? EventNoteOutlinedIcon : TableChartOutlinedIcon;
  return (
    <Tooltip
      placement="top"
      arrow
      title={
        <Box sx={{ py: 0.25, maxWidth: 260 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Icon sx={{ fontSize: 13, color: ACCENT }} />
            <Box component="span" sx={{ fontWeight: 600, color: INK }}>
              {source.title}
            </Box>
          </Box>
          <Box sx={{ color: MUTED }}>{source.detail}</Box>
          <Box sx={{ mt: 0.5, color: LINK, fontWeight: 500 }}>
            {source.kind === 'note' ? 'View the note' : 'View the data'} ↗
          </Box>
        </Box>
      }
    >
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={`Source: ${source.short ?? source.title}`}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          maxWidth: 170,
          height: 18,
          ml: 0.5,
          px: 0.6,
          verticalAlign: '0.08em',
          borderRadius: '6px',
          fontSize: FS.micro,
          fontWeight: 500,
          lineHeight: 1,
          color: MUTED,
          bgcolor: SURFACE,
          whiteSpace: 'nowrap',
          transition: 'background-color 120ms ease, color 120ms ease',
          '& .cite-go': { transition: 'transform 120ms ease' },
          '&:hover, &:focus-visible': {
            color: LINK,
            bgcolor: LINK_BG_HOVER,
            '& .cite-go': { transform: 'translate(1px, -1px)' },
          },
          '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
        }}
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {source.short ?? source.title}
        </Box>
        <NorthEastIcon className="cite-go" sx={{ fontSize: 10, flexShrink: 0 }} />
      </Box>
    </Tooltip>
  );
}

function stripCites(text: string): string {
  return text.replace(CITE_RE, '').trim();
}

/**
 * Inline text with citation chips. Each source gets one chip per answer,
 * at its first mention; the full list is under "Sources" anyway.
 */
function renderInline(text: string, props: Props, keyBase: string): ReactNode[] {
  const seen = props.seen ?? new Set<string>();
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(CITE_RE)) {
    const start = m.index ?? 0;
    // Keep closing punctuation with its sentence: "…this year [S1]." → "…this year. ↗"
    const end = start + m[0].length;
    const punct = /^[.,;:!?)]+/.exec(text.slice(end))?.[0] ?? '';
    const before = text.slice(last, start).replace(/\s+$/, '') + punct;
    if (before) out.push(...renderEmphasis(before, `${keyBase}-t${i}`));
    const ids = (m[1] ?? '').split(/\s*,\s*/);
    ids.forEach((id, j) => {
      const source = props.sources.get(id);
      if (source && !seen.has(id)) {
        seen.add(id);
        out.push(
          <CiteChip
            key={`${keyBase}-c${i}-${j}`}
            source={source}
            onClick={() => props.onCite(id)}
          />,
        );
      }
    });
    last = end + punct.length;
    i += 1;
  }
  if (last < text.length) out.push(...renderEmphasis(text.slice(last), `${keyBase}-t${i}`));
  return out;
}

/** Colour explicitly signed changes inside plain text. */
function renderSigned(text: string, keyBase: string): ReactNode[] {
  return text.split(SIGNED_RE).map((part, i) => {
    const sign = SIGNED_RE.test(part) ? signOf(part) : 0;
    SIGNED_RE.lastIndex = 0;
    if (!sign) return <Fragment key={`${keyBase}-s${i}`}>{part}</Fragment>;
    return (
      <Box
        component="span"
        key={`${keyBase}-s${i}`}
        sx={{ color: sign > 0 ? UP : DOWN, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
      >
        {part}
      </Box>
    );
  });
}

function renderEmphasis(text: string, keyBase: string): ReactNode[] {
  return text.split(INLINE_RE).map((part, i) => {
    const key = `${keyBase}-${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <Box component="strong" key={key} sx={{ fontWeight: 650, color: INK }}>
          {renderSigned(part.slice(2, -2), key)}
        </Box>
      );
    }
    if (
      part.length > 2 &&
      ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_')))
    ) {
      return (
        <Box component="em" key={key} sx={{ color: MUTED }}>
          {part.slice(1, -1)}
        </Box>
      );
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <Box
          component="code"
          key={key}
          sx={{ fontSize: '0.85em', px: 0.5, borderRadius: 0.75, bgcolor: SURFACE }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }
    return <Fragment key={key}>{renderSigned(part, key)}</Fragment>;
  });
}

// ---------------------------------------------------------------------
// Block parsing
// ---------------------------------------------------------------------
type CalloutTone = 'warning' | 'note';

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'h'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'table'; rows: string[][] }
  | { kind: 'kpi'; tiles: string[][] }
  | { kind: 'callout'; tone: CalloutTone; lines: string[] }
  | { kind: 'assumed'; text: string };

const ASSUMED_RE = /^[_*]?\s*assumed:\s*/i;

function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const at = (n: number): string => (lines[n] ?? '').trim();
  const bullet = /^[-*•]\s+/;
  const numbered = /^\d+[.)]\s+/;
  const startsBlock = /^(#{1,6}\s|\||[-*•]\s|\d+[.)]\s|:::)/;
  let i = 0;
  while (i < lines.length) {
    const trimmed = at(i);
    if (!trimmed) {
      i += 1;
      continue;
    }
    const fence = /^:::\s*(kpi|warning|note|tip|info)\s*$/i.exec(trimmed);
    if (fence) {
      const type = (fence[1] ?? '').toLowerCase();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^:::\s*$/.test(at(i))) {
        if (at(i)) body.push(at(i));
        i += 1;
      }
      i += 1; // closing :::
      if (type === 'kpi') {
        blocks.push({ kind: 'kpi', tiles: body.map((l) => l.split('|').map((c) => c.trim())) });
      } else {
        blocks.push({
          kind: 'callout',
          tone: type === 'warning' ? 'warning' : 'note',
          lines: body,
        });
      }
      continue;
    }
    if (/^:::\s*$/.test(trimmed)) {
      i += 1;
      continue;
    }
    if (/^#{1,6}\s/.test(trimmed)) {
      blocks.push({ kind: 'h', text: trimmed.replace(/^#{1,6}\s+/, '') });
      i += 1;
      continue;
    }
    if (ASSUMED_RE.test(trimmed)) {
      blocks.push({
        kind: 'assumed',
        text: trimmed.replace(ASSUMED_RE, '').replace(/[_*]\s*$/, ''),
      });
      i += 1;
      continue;
    }
    if (trimmed.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && at(i).startsWith('|')) {
        const cells = at(i)
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
        i += 1;
      }
      blocks.push({ kind: 'table', rows });
      continue;
    }
    if (bullet.test(trimmed) || numbered.test(trimmed)) {
      const kind = bullet.test(trimmed) ? 'ul' : 'ol';
      const re = kind === 'ul' ? bullet : numbered;
      const items: string[] = [];
      while (i < lines.length && re.test(at(i))) {
        items.push(at(i).replace(re, ''));
        i += 1;
      }
      blocks.push({ kind, items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && at(i) && !startsBlock.test(at(i)) && !ASSUMED_RE.test(at(i))) {
      para.push(at(i));
      i += 1;
    }
    blocks.push({ kind: 'p', lines: para });
  }
  return blocks;
}

// ---------------------------------------------------------------------
// Block renderers
// ---------------------------------------------------------------------
function KpiTiles({ tiles, props, k }: { tiles: string[][]; props: Props; k: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
        gap: 1,
      }}
    >
      {tiles.map((cells, ti) => {
        const [label = '', value = '', change = ''] = cells;
        const cites = [...(cells.join(' ').match(CITE_RE) ?? [])].join(' ');
        const delta = stripCites(change);
        const sign = signOf(delta);
        return (
          <Box
            key={`${k}-${ti}`}
            sx={{
              p: 1.25,
              borderRadius: '12px',
              background: `linear-gradient(180deg, ${PANEL} 0%, #fcfaff 100%)`,
              border: `1px solid ${LINE}`,
              boxShadow: '0 1px 2px rgba(24, 24, 27, 0.04)',
              minWidth: 0,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: FS.micro,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: MUTED,
              }}
            >
              {stripCites(label)}
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: '1.125rem',
                fontWeight: 650,
                letterSpacing: '-0.02em',
                color: INK,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.25,
              }}
            >
              {stripCites(value)}
            </Typography>
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 18 }}>
              {delta && (
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.1,
                    borderRadius: 999,
                    fontSize: FS.micro,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: sign > 0 ? UP : sign < 0 ? DOWN : MUTED,
                    bgcolor: sign > 0 ? '#ecfdf5' : sign < 0 ? '#fef2f2' : SURFACE,
                  }}
                >
                  {delta}
                </Box>
              )}
              <Box component="span" sx={{ fontSize: FS.sm }}>
                {renderInline(cites, props, `${k}-${ti}-c`)}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const CALLOUT = {
  warning: { Icon: WarningAmberRoundedIcon, fg: '#b45309', bg: '#fffbeb', edge: '#f59e0b' },
  note: { Icon: InfoOutlinedIcon, fg: '#1d4ed8', bg: '#eff6ff', edge: '#3b82f6' },
} as const;

function Callout({
  tone,
  lines,
  props,
  k,
}: {
  tone: CalloutTone;
  lines: string[];
  props: Props;
  k: string;
}) {
  const c = CALLOUT[tone];
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        px: 1.5,
        py: 1.1,
        borderRadius: '10px',
        bgcolor: c.bg,
        boxShadow: `inset 3px 0 0 ${c.edge}`,
      }}
    >
      <c.Icon sx={{ fontSize: 17, color: c.fg, mt: '2px', flexShrink: 0 }} />
      <Box sx={{ fontSize: FS.sm, lineHeight: 1.55, color: TEXT }}>
        {lines.map((l, li) => (
          <Box key={`${k}-${li}`} sx={{ '& + &': { mt: 0.5 } }}>
            {renderInline(l.replace(/^[-*•]\s+/, ''), props, `${k}-${li}`)}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DataTable({ rows, props, k }: { rows: string[][]; props: Props; k: string }) {
  const [head, ...body] = rows;
  const width = Math.max(head?.length ?? 0, ...body.map((r) => r.length));
  // A column is numeric when every non-empty body cell is a number.
  const numeric = Array.from({ length: width }, (_, ci) => {
    const cells = body.map((r) => stripCites(r[ci] ?? '')).filter(Boolean);
    return cells.length > 0 && cells.every((c) => NUMERIC_CELL.test(c));
  });
  const rankCol = (head?.[0] ?? '').trim() === '#' || /^rank$/i.test(head?.[0] ?? '');
  // The main measure gets a faint bar behind each value, so the table reads at a glance.
  const toNum = (c: string) =>
    Number(
      stripCites(c)
        .replace(/[,$%+]/g, '')
        .replace('−', '-'),
    );
  const barCol = numeric.findIndex(
    (isNum, ci) =>
      isNum &&
      !(rankCol && ci === 0) &&
      body.length > 2 &&
      body.every((r) => !signOf(stripCites(r[ci] ?? '')) && toNum(r[ci] ?? '') >= 0),
  );
  const barMax = barCol < 0 ? 0 : Math.max(...body.map((r) => toNum(r[barCol] ?? '') || 0));
  return (
    <Box
      sx={{ overflowX: 'auto', border: `1px solid ${LINE}`, borderRadius: '12px', bgcolor: PANEL }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: FS.table,
          fontVariantNumeric: 'tabular-nums',
          '& th': {
            fontWeight: 600,
            color: MUTED,
            fontSize: FS.micro,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            bgcolor: '#fafafa',
            px: 1.25,
            py: 0.85,
            whiteSpace: 'nowrap',
          },
          '& td': {
            px: 1.25,
            py: 0.8,
            borderTop: `1px solid ${LINE}`,
            color: INK,
            whiteSpace: 'nowrap',
          },
          '& tbody tr:hover td': { bgcolor: '#fafafa' },
          '& thead th': { borderBottom: `1px solid ${LINE}` },
        }}
      >
        {head && (
          <thead>
            <tr>
              {head.map((c, ci) => (
                <Box component="th" key={ci} sx={{ textAlign: numeric[ci] ? 'right' : 'left' }}>
                  {renderInline(c, props, `${k}-h${ci}`)}
                </Box>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => {
                const plain = stripCites(c);
                const sign = numeric[ci] ? signOf(plain) : 0;
                const isRank = rankCol && ci === 0;
                return (
                  <Box
                    component="td"
                    key={ci}
                    sx={{
                      textAlign: numeric[ci] ? 'right' : 'left',
                      ...(ci === barCol &&
                        barMax > 0 && {
                          backgroundImage: `linear-gradient(to left, ${ACCENT_SOFT} ${
                            ((toNum(c) || 0) / barMax) * 100
                          }%, transparent 0)`,
                          backgroundSize: 'calc(100% - 8px) 60%',
                          backgroundPosition: 'right 4px center',
                          backgroundRepeat: 'no-repeat',
                        }),
                      color: isRank ? SUBTLE : sign > 0 ? UP : sign < 0 ? DOWN : INK,
                      fontWeight: sign ? 600 : !numeric[ci] && ci === (rankCol ? 1 : 0) ? 500 : 400,
                      width: isRank ? 28 : undefined,
                    }}
                  >
                    {numeric[ci] && !c.match(CITE_RE)
                      ? plain
                      : renderInline(c, props, `${k}-${ri}-${ci}`)}
                  </Box>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

export function ChatMarkdown(outer: Props) {
  const props: Props = { ...outer, seen: new Set<string>() };
  const blocks = parseBlocks(props.text);
  const firstP = blocks.findIndex((b) => b.kind === 'p');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {blocks.map((b, bi) => {
        const key = `b${bi}`;
        switch (b.kind) {
          case 'h':
            return (
              <Typography
                key={key}
                sx={{
                  mt: 0.75,
                  mb: -0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontWeight: 650,
                  fontSize: FS.sm,
                  color: INK,
                  '&::before': {
                    content: '""',
                    width: 3,
                    height: 14,
                    borderRadius: 2,
                    bgcolor: ACCENT,
                    flexShrink: 0,
                  },
                }}
              >
                {renderInline(b.text, props, key)}
              </Typography>
            );
          case 'p': {
            // A short opening line is the answer's headline (ChatGPT / Perplexity style).
            // A longer opening paragraph is just body text — a bold wall reads as shouting.
            const headline =
              bi === firstP &&
              bi === 0 &&
              blocks.length > 1 &&
              b.lines.length === 1 &&
              stripCites(b.lines[0] ?? '').length <= HEADLINE_MAX;
            return (
              <Typography
                key={key}
                sx={{
                  fontSize: headline ? FS.body : FS.sm,
                  fontWeight: headline ? 600 : 400,
                  lineHeight: headline ? 1.5 : 1.65,
                  color: headline ? INK : TEXT,
                }}
              >
                {b.lines.map((l, li) => (
                  <Fragment key={`${key}-${li}`}>
                    {li > 0 && <br />}
                    {renderInline(l, props, `${key}-${li}`)}
                  </Fragment>
                ))}
              </Typography>
            );
          }
          case 'ul':
          case 'ol':
            return (
              <Box
                key={key}
                component={b.kind}
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: 'none',
                  counterReset: 'abhi',
                  fontSize: FS.sm,
                  lineHeight: 1.6,
                  color: TEXT,
                  '& li': { position: 'relative', pl: b.kind === 'ol' ? 3.5 : 2.25 },
                  '& li + li': { mt: 0.75 },
                  '& li::before':
                    b.kind === 'ol'
                      ? {
                          counterIncrement: 'abhi',
                          content: 'counter(abhi)',
                          position: 'absolute',
                          left: 0,
                          top: '1px',
                          width: 20,
                          height: 20,
                          borderRadius: '6px',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: ACCENT_TINT,
                          color: ACCENT_DEEP,
                          fontSize: FS.micro,
                          fontWeight: 650,
                          fontVariantNumeric: 'tabular-nums',
                        }
                      : {
                          content: '""',
                          position: 'absolute',
                          left: 4,
                          top: '0.62em',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: ACCENT,
                          opacity: 0.55,
                        },
                }}
              >
                {b.items.map((it, ii) => (
                  <li key={`${key}-${ii}`}>{renderInline(it, props, `${key}-${ii}`)}</li>
                ))}
              </Box>
            );
          case 'kpi':
            return <KpiTiles key={key} tiles={b.tiles} props={props} k={key} />;
          case 'callout':
            return <Callout key={key} tone={b.tone} lines={b.lines} props={props} k={key} />;
          case 'assumed':
            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'flex-start',
                  fontSize: FS.label,
                  lineHeight: 1.5,
                  color: MUTED,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14, mt: '2px', color: SUBTLE }} />
                <span>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Assumed:
                  </Box>{' '}
                  {renderInline(b.text, props, key)}
                </span>
              </Box>
            );
          case 'table':
            return <DataTable key={key} rows={b.rows} props={props} k={key} />;
        }
      })}
    </Box>
  );
}
